import React, { FC, useCallback, useMemo } from 'react';
import { useLocalStorageState } from 'ahooks';
import { useTranslation } from 'react-i18next';
import './index.scss';
import { RootState } from '@whelk/store';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { ModelCloseIcon } from '@whelk/assets/src/web/common-icons';
import { ISection, SectionType } from '@whelk/services/src/type/chat';
import ResponsiveModal from '../../ResponsiveModal';
import { assetUrl } from '@whelk/const';

interface IModalTitleProps {
  title: string;
  handleCancel: () => void;
}

interface IModalStorageData {
  lastShownDate: number; // 上次显示日期时间戳
  lastShownVersion: number; // 上次显示的版本号
}

// 默认空数据
const DEFAULT_MODAL_DATA: IModalStorageData = {
  lastShownDate: 0,
  lastShownVersion: 0,
};

// 模态框标题组件
const ModalTitle: FC<IModalTitleProps> = ({ title, handleCancel }) => {
  return (
    <header className='mb-[16px] flex w-full items-center justify-between md:mb-[16px]'>
      <h1 className='text-text_00 text-[16px] font-[600] leading-normal md:text-[20px]'>{title}</h1>
      <div
        onClick={handleCancel}
        className='md:hover:bg-col_text05 text-col_text02 md:text-col_text03 z-50 flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded '
      >
        <ModelCloseIcon />
      </div>
    </header>
  );
};

// 订阅直播模态框组件
const NotificationLiveModal: FC = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const isLogin = useSelector((state: RootState) => state.user.isLogin);
  const { notificationLiveModalConfig } = useSelector((state: RootState) => state.config);
  // 存储弹窗显示信息
  const [modalData, setModalData] = useLocalStorageState<IModalStorageData>(
    'chat_notification_modal_data',
    {
      defaultValue: DEFAULT_MODAL_DATA,
    },
  );

  const renderSection = (section: ISection) => {
    if (!section) {
      return null;
    }

    const { type, content, children } = section;

    switch (type) {
      case SectionType.Title:
        return (
          <h2
            className='text-col_text00 mb-[16px] text-[18px] font-[600] leading-normal'
            dangerouslySetInnerHTML={{ __html: t(content) }}
          />
        );
      case SectionType.Text:
        return (
          <p
            className='text-col_text00 mb-[16px] text-[12px] font-[300] leading-normal md:text-[13px]'
            dangerouslySetInnerHTML={{ __html: t(content) }}
          />
        );
      case SectionType.Description:
        return (
          <p
            className='text-col_text06 mb-[13px] text-[12px] font-[300] leading-normal md:text-[12px]'
            dangerouslySetInnerHTML={{ __html: t(content) }}
          />
        );
      case SectionType.List:
        return (
          <section className='md:mb-[16px]'>
            <h3
              className='text-col_text00 mb-[12px] text-[12px] font-[500] leading-normal md:text-[13px]'
              dangerouslySetInnerHTML={{ __html: t(content) }}
            />
            <ul className='flex list-outside list-disc flex-col gap-[12px] pl-[20px]'>
              {children?.map((child) => renderSection(child))}
            </ul>
          </section>
        );
      case SectionType.ListItem:
        return (
          <li
            className='text-col_text00 text-[12px] font-[300] leading-normal md:text-[13px]'
            dangerouslySetInnerHTML={{ __html: t(content) }}
          />
        );
    }
  };

  const LeftImg = useMemo(() => {
    return `${assetUrl}notification_modal.png?x-oss-process=image/format,webp`;
  }, []);

  const {
    title = '',
    sections = [],
    buttonText = '',
    buttonLink = '',
  } = notificationLiveModalConfig || {};

  const showModal = useMemo(() => {
    if (!isLogin) {
      return false;
    }
    if (!notificationLiveModalConfig) {
      return false;
    }

    const { lastShownVersion = 0 } = modalData || {};
    // 如果版本号大于上次显示的版本号，则显示模态框
    if (notificationLiveModalConfig.version > lastShownVersion) {
      return true;
    }

    return false;
  }, [notificationLiveModalConfig, modalData, isLogin]);

  const handleCancel = useCallback(() => {
    setModalData({
      lastShownDate: dayjs().valueOf(),
      lastShownVersion: notificationLiveModalConfig?.version || 0,
    });
  }, [setModalData, notificationLiveModalConfig]);

  const handleButtonClick = useCallback(() => {
    if (buttonLink) router.push(buttonLink);
    handleCancel();
  }, [router, buttonLink, handleCancel]);

  return (
    <ResponsiveModal open={showModal} onCancel={handleCancel} className='live-modal'>
      <div className='notification-model flex items-center justify-center'>
        <div
          className='hidden h-[474px] w-[300px] flex-shrink-0 md:block'
          style={{
            backgroundImage: `url(${LeftImg})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        ></div>
        <article className='text-col_text00 flex flex-col md:h-[474px] md:p-[28px]'>
          <ModalTitle title={t(title)} handleCancel={handleCancel} />
          <section className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
            {sections.map((section) => renderSection(section))}
          </section>
          <button
            type='button'
            className='text-col_text05  bg-col_brand04  mt-[16px] flex h-[40px] w-full flex-shrink-0 cursor-pointer items-center justify-center self-end rounded-full text-[14px] font-[500] transition-colors hover:bg-[#6FB3FC] md:mt-[12px] md:w-[fit-content] md:px-[20px]'
            onClick={handleButtonClick}
          >
            {t(buttonText)}
          </button>
        </article>
      </div>
    </ResponsiveModal>
  );
};

export default NotificationLiveModal;
